import { CircularProgress, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { BoxButtonApp, ContainerApp } from '../components/custom/CustomComponents';
import DatosPartido from '../components/partidos/DatosPartido';
import JugadoresPartido from '../components/partidos/JugadoresPartido';
import { Context } from '../service/Context';
import { capitalizar } from '../utils/UtilesComun';

/**
 * Pagina de un partido.
 */
const Partido = () => {
  const { obtenerJugadoresPartido, obtenerDatosPartido, partidosCache, setPartidoCacheItem } = useContext(Context);
  const { routeIdPartido } = useParams();

  const cachedPartido = partidosCache?.[routeIdPartido];

  const [datosPartido, setDatosPartido] = useState(cachedPartido?.datosPartido);
  const [equipoLocal, setEquipoLocal] = useState(cachedPartido?.equipoLocal);
  const [equipoVisitante, setEquipoVisitante] = useState(cachedPartido?.equipoVisitante);
  const [loadingDatos, setLoadingDatos] = useState(!cachedPartido?.datosPartido);
  const [loadingLocal, setLoadingLocal] = useState(!cachedPartido?.equipoLocal);
  const [loadingVisitante, setLoadingVisitante] = useState(!cachedPartido?.equipoVisitante);

  // Sincronizar estado inmediatamente durante render al cambiar de partido en la URL
  const [prevRouteId, setPrevRouteId] = useState(routeIdPartido);
  if (prevRouteId !== routeIdPartido) {
    setPrevRouteId(routeIdPartido);
    const cached = partidosCache?.[routeIdPartido];
    if (cached?.datosPartido) {
      setDatosPartido(cached.datosPartido);
      setEquipoLocal(cached.equipoLocal);
      setEquipoVisitante(cached.equipoVisitante);
      setLoadingDatos(false);
      setLoadingLocal(!cached.equipoLocal);
      setLoadingVisitante(!cached.equipoVisitante);
    } else {
      setDatosPartido(undefined);
      setEquipoLocal(undefined);
      setEquipoVisitante(undefined);
      setLoadingDatos(true);
      setLoadingLocal(true);
      setLoadingVisitante(true);
    }
  }

  // Cargar datos desde la API con revalidación en segundo plano y caché SWR
  useEffect(() => {
    let cancelada = false;

    const cargarDatos = async () => {
      if (!routeIdPartido) {
        return;
      }

      const cached = partidosCache?.[routeIdPartido];
      if (!cached?.datosPartido) {
        setLoadingDatos(true);
      }
      if (!cached?.equipoLocal) {
        setLoadingLocal(true);
      }
      if (!cached?.equipoVisitante) {
        setLoadingVisitante(true);
      }

      try {
        const datos = await obtenerDatosPartido(routeIdPartido);
        if (cancelada || !datos) {
          if (!cancelada) {
            setLoadingDatos(false);
            setLoadingLocal(false);
            setLoadingVisitante(false);
          }
          return;
        }

        setDatosPartido(datos);
        setPartidoCacheItem(routeIdPartido, { datosPartido: datos });
        setLoadingDatos(false);

        // Cargar jugadores locales y visitantes de forma concurrente e independiente
        obtenerJugadoresPartido(routeIdPartido, datos.idLocal, 1)
          .then((local) => {
            if (cancelada) {
              return;
            }
            setEquipoLocal(local || []);
            setPartidoCacheItem(routeIdPartido, { equipoLocal: local || [] });
            setLoadingLocal(false);
          })
          .catch((err) => {
            console.error('Error al cargar jugadores locales:', err);
            if (!cancelada) {
              setLoadingLocal(false);
            }
          });

        obtenerJugadoresPartido(routeIdPartido, datos.idVisitante, 0)
          .then((visitante) => {
            if (cancelada) {
              return;
            }
            setEquipoVisitante(visitante || []);
            setPartidoCacheItem(routeIdPartido, { equipoVisitante: visitante || [] });
            setLoadingVisitante(false);
          })
          .catch((err) => {
            console.error('Error al cargar jugadores visitantes:', err);
            if (!cancelada) {
              setLoadingVisitante(false);
            }
          });
      } catch (error) {
        console.error('Error al cargar datos del partido:', error);
        if (!cancelada) {
          setLoadingDatos(false);
          setLoadingLocal(false);
          setLoadingVisitante(false);
        }
      }
    };

    cargarDatos();

    return () => {
      cancelada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIdPartido]);

  const titularesLocal = equipoLocal ? equipoLocal.filter((jugador) => jugador.titular) : [];
  const suplentesLocal = equipoLocal ? equipoLocal.filter((jugador) => !jugador.titular) : [];
  const titularesVisitante = equipoVisitante ? equipoVisitante.filter((jugador) => jugador.titular) : [];
  const suplentesVisitante = equipoVisitante ? equipoVisitante.filter((jugador) => !jugador.titular) : [];

  const handleObservacionUpdateLocal = (idJugador, updatedObservaciones) => {
    setEquipoLocal((prev) => {
      const updated = prev
        ? prev.map((jugador) =>
            jugador.idJugador === idJugador ? { ...jugador, observaciones: updatedObservaciones } : jugador
          )
        : [];
      setPartidoCacheItem(routeIdPartido, { equipoLocal: updated });
      return updated;
    });
  };

  const handleObservacionUpdateVisitante = (idJugador, updatedObservaciones) => {
    setEquipoVisitante((prev) => {
      const updated = prev
        ? prev.map((jugador) =>
            jugador.idJugador === idJugador ? { ...jugador, observaciones: updatedObservaciones } : jugador
          )
        : [];
      setPartidoCacheItem(routeIdPartido, { equipoVisitante: updated });
      return updated;
    });
  };

  return (
    <ContainerApp maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4.75}>
          <Grid container direction="column" spacing={1.5}>
            <Grid item>
              {datosPartido?.idLocal ? (
                <Link to={`/equipo/${datosPartido.idLocal}`}>
                  <BoxButtonApp>{capitalizar(datosPartido.nombreLocal)}</BoxButtonApp>
                </Link>
              ) : (
                <BoxButtonApp sx={{ opacity: 0.6, pointerEvents: 'none' }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                </BoxButtonApp>
              )}
            </Grid>
            <Grid item>
              <JugadoresPartido
                jugadores={titularesLocal}
                titulo="Titulares"
                loading={loadingLocal}
                onUpdate={handleObservacionUpdateLocal}
              />
            </Grid>
            <Grid item>
              <JugadoresPartido
                jugadores={suplentesLocal}
                titulo="Suplentes"
                loading={loadingLocal}
                onUpdate={handleObservacionUpdateLocal}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={2.5}>
          <DatosPartido datosPartido={datosPartido} loading={loadingDatos} />
        </Grid>
        <Grid item xs={12} md={4.75}>
          <Grid container direction="column" spacing={1.5}>
            <Grid item>
              {datosPartido?.idVisitante ? (
                <Link to={`/equipo/${datosPartido.idVisitante}`}>
                  <BoxButtonApp>{capitalizar(datosPartido.nombreVisitante)}</BoxButtonApp>
                </Link>
              ) : (
                <BoxButtonApp sx={{ opacity: 0.6, pointerEvents: 'none' }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                </BoxButtonApp>
              )}
            </Grid>
            <Grid item>
              <JugadoresPartido
                jugadores={titularesVisitante}
                titulo="Titulares"
                loading={loadingVisitante}
                onUpdate={handleObservacionUpdateVisitante}
              />
            </Grid>
            <Grid item>
              <JugadoresPartido
                jugadores={suplentesVisitante}
                titulo="Suplentes"
                loading={loadingVisitante}
                onUpdate={handleObservacionUpdateVisitante}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ContainerApp>
  );
};

export default Partido;
